import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../API';
import styles from '../../pages/admin/Config.module.css';

/**
 * Hub-only controls for the network ID allocation watermark.
 */
export default function HubAllocationPanel() {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftValue, setDraftValue] = useState('');
    const [error, setError] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['hub-allocation'],
        queryFn: () => api.get('network/hub/allocation'),
        retry: false,
    });

    const allocation = data?.status === 200 && data?.data?.success !== false
        ? data.data
        : null;
    const loadError = data?.status === 200 && data?.data?.success === false
        ? data.data.error
        : null;

    const resetMutation = useMutation({
        mutationFn: async (highestNetworkId) => {
            const response = await api.put('network/hub/allocation', {
                query: { highestNetworkId: String(highestNetworkId) },
            });
            if (response.status !== 200 || response.data?.success === false) {
                throw new Error(response.data?.error || 'Failed to update allocation');
            }
            return response.data;
        },
        onSuccess: async () => {
            setDialogOpen(false);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['hub-allocation'] });
            await queryClient.invalidateQueries({ queryKey: ['local-info'] });
        },
        onError: (err) => {
            setError(err.message || 'Failed to update allocation');
        },
    });

    if (isLoading) {
        return <div className={styles.preview}>Loading allocation…</div>;
    }
    if (loadError || !allocation) {
        return null;
    }

    const openReset = (useAuto) => {
        setError(null);
        setDraftValue(
            useAuto
                ? String(allocation.maxAssignedNumericId ?? 0)
                : String(allocation.highestNetworkId ?? 0)
        );
        setDialogOpen(true);
    };

    return (
        <div style={{ marginTop: '1.5em' }} data-sourcepanel="HubAllocationPanel.js">
            <label className={styles.label}>Spoke ID counter</label>
            <div className={styles.preview} style={{ marginBottom: '0.75em' }}>
                <div className={styles.previewContent}>
                    Current watermark: <strong>{allocation.highestNetworkId}</strong>
                    {' · '}
                    Next assigned ID: <strong>{allocation.nextNumericId}</strong>
                    {' · '}
                    Max in use: <strong>{allocation.maxAssignedNumericId}</strong>
                </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75em', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className={styles.button}
                    onClick={() => openReset(true)}
                >
                    Reset counter to max assigned
                </button>
                <button
                    type="button"
                    className={styles.button}
                    onClick={() => openReset(false)}
                >
                    Set counter…
                </button>
            </div>

            {dialogOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    onClick={() => !resetMutation.isPending && setDialogOpen(false)}
                >
                    <div
                        style={{
                            background: '#fff',
                            padding: '1.5em',
                            borderRadius: 8,
                            maxWidth: 420,
                            width: '90%',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0 }}>Set spoke ID watermark</h3>
                        <p style={{ fontSize: '0.9em', color: '#444' }}>
                            New spokes receive <code>highestNetworkId + 1</code>.
                            The value cannot be below the highest ID already assigned
                            ({allocation.maxAssignedNumericId}).
                        </p>
                        <label className={styles.label} htmlFor="hubAllocationValue">
                            highestNetworkId
                        </label>
                        <input
                            id="hubAllocationValue"
                            className={styles.input}
                            type="number"
                            min={allocation.maxAssignedNumericId ?? 0}
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                        />
                        {error && (
                            <div style={{ color: '#b00020', marginTop: '0.75em', fontSize: '0.9em' }}>
                                {error}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '0.75em', marginTop: '1.25em', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className={styles.button}
                                disabled={resetMutation.isPending}
                                onClick={() => setDialogOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.button}
                                disabled={resetMutation.isPending}
                                onClick={() => resetMutation.mutate(draftValue)}
                            >
                                {resetMutation.isPending ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
